import React, { useMemo } from 'react';
import Workspace from '../../components/Workspace/Workspace';
import { SalesInvoice } from '../../graphql/entities';
import Formatters from '../../components/grid-addons/formatters';
import { usePredefinedFilters } from '../../getPredefinedFilters';
import { useWorkspace } from '../../graphql/hooks/useWorkspace';
import { useAttributesForWorkspace } from '../../components/Workspace/useAttributesForWorkspace';
import { Direction, ISalesInvoice } from '../../graphql/schema/p4types';
import salesAttributeQuery from '../../graphql/queries/sales/SalesAttributeConfigListQuery.graphql';
import customerAttributeQuery from '../../graphql/queries/customer/CustomerAttributeConfigListQuery.graphql';
import productAttributeQuery from '../../graphql/queries/product/ProductAttributeConfigListQuery.graphql';
import { Paths } from '../../@types/keyOfNested';
import { useSalesInvoiceWorkspaceActions } from '../../actions/salesLedger/salesInvoice/SalesInvoiceWorkspaceAction';
import { WorkspaceActionButtons } from '../../components/Workspace/WorkspaceActionButtons';
import { IWorkspaceColumnOverride } from '../../@types/workspaceTypes';
import { useAbstractSalesHeaderColumnOverrides } from '../../utils/abstractSalesHooks';
import { ModalFormatter } from '../../components/grid-addons/formatters/ModalFormatter';
import { IEditableDataGridColumnDef } from '../../@types/editableDataGridTypes';

const LazyCustomerForm: any = React.lazy(() => import('../../views/customer/CustomerForm'));

const { readOnlyStatus, formUrl, type, title, workspace } = SalesInvoice;

const SALES_INVOICE_WORKSPACE_FIELD_NAMES: Array<Paths<ISalesInvoice>> = [
  'documentNumber',
  'documentDate',
  'documentStatus',
  'customer.reference',
  'customer.name',
  'documentReference',
  'paymentStatus',
  'headerTotal',
];

const SALES_INVOICE_COLUMN_OVERRIDES: IWorkspaceColumnOverride = {
  documentNumber: {
    name: 'Number',
    key: 'documentNumber',
    width: 100,
  },
  documentDate: {
    name: 'Date',
    key: 'documentDate',
    formatter: Formatters.DateFormatter,
    width: 110,
  },
  headerTotal: {
    name: 'Invoice Total',
    width: 150,
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  'customer.reference': {
    name: 'Customer',
    formatter: ModalFormatter,
    width: 125,
    options: {
      modalContent: LazyCustomerForm,
      modalProps: { forceReadOnly: true },
      entityId: 'customer.id',
    },
  },
  documentStatus: {
    name: 'Status',
    formatter: Formatters.DocumentStatusFormatter,
    width: 100,
  },
  paymentStatus: {
    name: 'Payment',
    formatter: Formatters.PaymentStatusFormatter,
    width: 110,
  },
  'customer.customerInvoiceDocumentDefault.output': {
    name: 'Document Output',
  },
  'deliveryAddress.country.description': {
    name: 'Delivery - Country Name',
  },
  'amazonSalesInvoiceEmbeddable.amazonCarriage': {
    name: 'Amazon Carriage',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  'amazonSalesInvoiceEmbeddable.amazonCommission': {
    name: 'Amazon Commission',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  'amazonSalesInvoiceEmbeddable.amazonFbaFees': {
    name: 'Amazon FBA Fees',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  'amazonSalesInvoiceEmbeddable.amazonProfitMargin': {
    name: 'Amazon Profit Margin',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  'amazonSalesInvoiceEmbeddable.amazonProfitValue': {
    name: 'Amazon Profit Value',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  'amazonSalesInvoiceEmbeddable.amazonTotalFees': {
    name: 'Amazon Total Fees',
    formatter: Formatters.NumericFormatter,
    options: {
      format: { decimals: 2 },
    },
  },
  'customer.customerAccountManagementGroup.description': {
    name: 'Customer Account Management Group',
  },
  'deliveryCustomer.customerAccountManagementGroup.description': {
    name: 'Delivery Customer Account Management Group',
  },
};

const SalesInvoiceWorkspaceActions = (props) => {
  const [testWorkspaceActions, modals] = useSalesInvoiceWorkspaceActions();
  return (
    <>
      {modals}
      <WorkspaceActionButtons {...props} actionButtons={testWorkspaceActions} />
    </>
  );
};

const filtersFilter = (column: IEditableDataGridColumnDef) => {
  return (
    column?.fieldName !== 'deliveryAddress.line1' &&
    column?.fieldName !== 'deliveryAddress.line2' &&
    column?.fieldName !== 'deliveryAddress.line3' &&
    column?.fieldName !== 'deliveryAddress.line4' &&
    column?.fieldName !== 'deliveryAddress.line5' &&
    column?.fieldName !== 'deliveryAddress.postCode' &&
    column?.fieldName !== 'deliveryAddress.country' &&
    column?.fieldName !== 'deliveryName' &&
    column?.fieldName !== 'deliveryPhoneNumber' &&
    column?.fieldName !== 'deliveryMobileNumber' &&
    column?.fieldName !== 'deliveryEmail' &&
    column?.fieldName !== 'invoiceAddress.line1' &&
    column?.fieldName !== 'invoiceAddress.line2' &&
    column?.fieldName !== 'invoiceAddress.line3' &&
    column?.fieldName !== 'invoiceAddress.line4' &&
    column?.fieldName !== 'invoiceAddress.line5' &&
    column?.fieldName !== 'invoiceAddress.postCode' &&
    column?.fieldName !== 'invoiceAddress.country' &&
    column?.fieldName !== 'invoiceAddress.country.isoCountry' &&
    column?.fieldName !== 'invoiceCompany'
  );
};

const SalesInvoiceWorkspace = (props) => {
  const predefinedFilters = usePredefinedFilters(props);

  const salesAttributeColumns = useAttributesForWorkspace(salesAttributeQuery);

  const customerAttributeColumns = useAttributesForWorkspace(customerAttributeQuery, {
    valuesPath: 'customer.attributeValues',
    columnKey: 'customer.attributeValues.attributeConfig.title',
  });

  const productAttributeColumns = useAttributesForWorkspace(productAttributeQuery, {
    valuesPath: 'product.attributeValues',
    columnKey: 'product.attributeValues.attributeConfig.title',
  });

  const customColumns = useMemo(
    () => ({ ...salesAttributeColumns, ...customerAttributeColumns, ...productAttributeColumns }),
    [salesAttributeColumns, customerAttributeColumns, productAttributeColumns]
  );

  const salesInvoiceColumnOverrides = useAbstractSalesHeaderColumnOverrides(SALES_INVOICE_COLUMN_OVERRIDES);

  const workspaceProps = useWorkspace<ISalesInvoice>(props, SalesInvoice, {
    filters: predefinedFilters,
    columns: SALES_INVOICE_WORKSPACE_FIELD_NAMES,
    columnOverrides: salesInvoiceColumnOverrides,
    customColumns: customColumns,
    initialSortColumn: 'documentNumber',
    initialSortDirection: Direction.Desc,
  });

  return (
    <Workspace
      entityType={type}
      title={title}
      formUrl={formUrl}
      defaultQuery={workspace}
      readOnlyStatus={readOnlyStatus}
      {...workspaceProps}
      {...props}
      entity={SalesInvoice}
      availableFiltersFilter={filtersFilter}
      workspaceActionButtonsHOC={SalesInvoiceWorkspaceActions}
    />
  );
};

SalesInvoiceWorkspace.defaultProps = {
  predefinedFilters: [],
};

export default SalesInvoiceWorkspace;
